import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { StripeElementsOptions } from '@stripe/stripe-js';
import {
  EligiblePaymentMethodType,
  GetPayInContextResponse,
  PayInContext,
} from 'shared/types/checkout';
import { sdk } from 'sdk';
import { readMarketplaceFxQuote } from 'utils/marketplace-fx-quote';
import newrelic from 'utils/monitoring/newrelicWrapper';

/**
 * Cart-currency amount used to keep Elements mountable when the server context
 * is unreachable. Settlement stays in the cart currency, never a local one.
 */
export type PayInContextFallback = {
  amount?: number;
  currency?: string;
};

export type UsePayInContextArgs = {
  cartId?: string;
  selectedCurrency?: string;
  showUpi?: boolean;
  /**
   * Shopper's billing country from the custom checkout form. UPI is India-only,
   * so this narrows eligiblePaymentMethodTypes client-side without adding
   * country to the server pay-in context request.
   */
  billingCountry?: string;
  enabled?: boolean;
  fallback?: PayInContextFallback;
};

export type UsePayInContextResult = {
  payInContext: PayInContext | null;
  stripeOptions: StripeElementsOptions | null;
  elementsKey: string | null;
  isLoading: boolean;
  error: string | null;
  /**
   * Elements are running on the cart-currency fallback because the server
   * context failed. Callers must settle in the cart currency while this is set.
   */
  isFallback: boolean;
  refetch: () => Promise<void>;
};

const ELIGIBLE_PAYMENT_METHOD_TYPES = new Set<string>(['card', 'upi']);

const isEligiblePaymentMethodType = (
  value: unknown,
): value is EligiblePaymentMethodType =>
  typeof value === 'string' && ELIGIBLE_PAYMENT_METHOD_TYPES.has(value);

const isValidPayInContext = (data: unknown): data is PayInContext => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const candidate = data as Partial<PayInContext>;
  return (
    typeof candidate.payableCurrency === 'string' &&
    candidate.payableCurrency.length > 0 &&
    typeof candidate.payableAmount === 'number' &&
    Number.isFinite(candidate.payableAmount) &&
    Array.isArray(candidate.eligiblePaymentMethodTypes) &&
    candidate.eligiblePaymentMethodTypes.length > 0 &&
    candidate.eligiblePaymentMethodTypes.every(isEligiblePaymentMethodType) &&
    typeof candidate.cartId === 'string' &&
    candidate.cartId.length > 0
  );
};

// UPI is India-only. Country was never part of the server-side showUpi +
// INR + amount-range gate, so it is narrowed here, client-side only.
const filterEligiblePaymentMethodTypes = (
  eligiblePaymentMethodTypes: EligiblePaymentMethodType[],
  billingCountry?: string,
): EligiblePaymentMethodType[] =>
  billingCountry === 'IN'
    ? eligiblePaymentMethodTypes
    : eligiblePaymentMethodTypes.filter((type) => type !== 'upi');

const buildStripeOptions = (
  context: PayInContext,
  billingCountry?: string,
): StripeElementsOptions =>
  ({
    mode: 'payment',
    amount: context.payableAmount,
    currency: context.payableCurrency.toLowerCase(),
    paymentMethodCreation: 'manual',
    paymentMethodTypes: filterEligiblePaymentMethodTypes(
      context.eligiblePaymentMethodTypes,
      billingCountry,
    ),
  }) satisfies StripeElementsOptions;

// Deliberately excludes billingCountry/paymentMethodTypes: this key forces a
// hard remount of everything StripeFormProvider wraps, including the address
// form that owns the country field. Folding country in here caused a
// remount-wipes-country feedback loop (selecting India reset the form back to
// blank, which reverted the selection). paymentMethodTypes changes propagate
// reactively via Stripe's own elements.update() when the options prop
// changes, without needing a remount, so only truly-instance-breaking
// dimensions (currency, amount, fx quote) belong in this key.
export const buildElementsRemountKey = (context: PayInContext): string =>
  [
    context.payableCurrency,
    context.payableAmount,
    context.fxQuoteId ?? 'none',
  ].join(':');

const buildFallbackStripeOptions = (
  amount?: number,
  currency?: string,
): StripeElementsOptions | null => {
  if (
    typeof amount !== 'number' ||
    !Number.isFinite(amount) ||
    amount <= 0 ||
    !currency
  ) {
    return null;
  }

  return {
    mode: 'payment',
    amount,
    currency: currency.toLowerCase(),
    paymentMethodCreation: 'manual',
    paymentMethodTypes: ['card'],
  } satisfies StripeElementsOptions;
};

const reportPayInContextError = (
  errorMessage: string,
  {
    cartId,
    selectedCurrency,
    error,
    responseData,
  }: {
    cartId?: string;
    selectedCurrency?: string;
    error?: unknown;
    responseData?: unknown;
  },
) => {
  datadogLogs.logger.error('Pay-in Context API Error', {
    error: errorMessage,
    errorDetails: error,
    cartId,
    selectedCurrency,
    action: 'stripe/getPayInContext',
    ...(responseData !== undefined && { responseData }),
  });
  newrelic.noticeError(
    error instanceof Error ? error : new Error(errorMessage),
    {
      cartId,
      selectedCurrency,
      action: 'stripe/getPayInContext',
    },
  );
};

/**
 * Fetches server-authoritative pay-in context for Stripe Elements.
 * Do not mount Elements until stripeOptions is non-null; remount when elementsKey changes.
 * When the fetch fails and a fallback is supplied, Elements fall back to the cart
 * currency so checkout stays payable — callers must then settle in that currency.
 */
export const usePayInContext = ({
  cartId,
  selectedCurrency,
  showUpi,
  billingCountry,
  enabled = true,
  fallback,
}: UsePayInContextArgs): UsePayInContextResult => {
  const [payInContext, setPayInContext] = useState<PayInContext | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const fetchPayInContext = useCallback(async () => {
    if (!enabled || !cartId) {
      // Invalidate in-flight requests so stale responses cannot remount Elements.
      requestIdRef.current += 1;
      setPayInContext(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    const marketplaceFxQuote = readMarketplaceFxQuote();

    try {
      const response = await sdk.callAction<GetPayInContextResponse>({
        actionName: 'stripe/getPayInContext',
        payload: {
          cartId,
          ...(selectedCurrency && { selectedCurrency }),
          showUpi: showUpi === true,
          ...(marketplaceFxQuote && { marketplaceFxQuote }),
        },
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (response.isError) {
        const errorMessage = `Pay-in context unavailable: ${
          response.error?.message ?? 'Unknown error'
        }`;

        reportPayInContextError(errorMessage, {
          cartId,
          selectedCurrency,
          error: response.error,
        });

        setPayInContext(null);
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      // Transport can succeed while the action body is a WithSDKError.
      if (response.data?.isError) {
        const actionError = response.data.error;
        const errorMessage = `Pay-in context unavailable: ${
          actionError?.message ?? 'Unknown error'
        }`;

        reportPayInContextError(errorMessage, {
          cartId,
          selectedCurrency,
          error: actionError,
        });

        setPayInContext(null);
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      if (!isValidPayInContext(response.data)) {
        const errorMessage = 'No pay-in context received from server';
        reportPayInContextError(errorMessage, {
          cartId,
          selectedCurrency,
          responseData: response.data,
        });
        setPayInContext(null);
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      setPayInContext(response.data);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      datadogLogs.logger.error('Pay-in Context Request Exception', {
        error: errorMessage,
        cartId,
        selectedCurrency,
        stack: err instanceof Error ? err.stack : undefined,
      });
      newrelic.noticeError(
        err instanceof Error ? err : new Error(errorMessage),
        {
          cartId,
          selectedCurrency,
          action: 'stripe/getPayInContext',
        },
      );
      setPayInContext(null);
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [cartId, selectedCurrency, showUpi, enabled]);

  useEffect(() => {
    fetchPayInContext().catch(() => {
      // Errors are stored in hook state
    });
  }, [fetchPayInContext]);

  // Refetch when FX quote is refreshed after expiry (same event as useFxRates consumers).
  useEffect(() => {
    if (!enabled || !cartId) {
      return undefined;
    }

    const handleConversionContextRefreshed = (event: Event) => {
      const customEvent = event as CustomEvent<{
        cartId: string;
        currency: string;
      }>;
      const { cartId: eventCartId, currency } = customEvent.detail;
      if (
        eventCartId === cartId &&
        (!selectedCurrency ||
          selectedCurrency.toUpperCase() === currency.toUpperCase())
      ) {
        fetchPayInContext().catch(() => {
          // Errors are stored in hook state
        });
      }
    };

    window.addEventListener(
      'conversionContextRefreshed',
      handleConversionContextRefreshed,
    );
    return () => {
      window.removeEventListener(
        'conversionContextRefreshed',
        handleConversionContextRefreshed,
      );
    };
  }, [cartId, selectedCurrency, enabled, fetchPayInContext]);

  const fallbackAmount = fallback?.amount;
  const fallbackCurrency = fallback?.currency;

  const fallbackStripeOptions = useMemo(
    () => buildFallbackStripeOptions(fallbackAmount, fallbackCurrency),
    [fallbackAmount, fallbackCurrency],
  );

  const isFallback = Boolean(error) && !payInContext && !!fallbackStripeOptions;

  const stripeOptions = useMemo(() => {
    if (payInContext) {
      return buildStripeOptions(payInContext, billingCountry);
    }
    return isFallback ? fallbackStripeOptions : null;
  }, [payInContext, isFallback, fallbackStripeOptions, billingCountry]);

  const elementsKey = useMemo(() => {
    if (payInContext) {
      return buildElementsRemountKey(payInContext);
    }
    return isFallback
      ? ['fallback', fallbackCurrency, fallbackAmount].join(':')
      : null;
  }, [payInContext, isFallback, fallbackCurrency, fallbackAmount]);

  return {
    payInContext,
    stripeOptions,
    elementsKey,
    isLoading,
    error,
    isFallback,
    refetch: fetchPayInContext,
  };
};
